import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAPI} from '../hooks/useApi';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { get, post } = useAPI();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Fetch user data if we have a token
      get('/api/auth/me')
        .then(data => {
          setUser(data);
        })
        .catch(() => {
          // If token is invalid, clear it
          localStorage.removeItem('token');
          setUser(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [get]);

  const register = async (name, email, password) => {
    try {
      const response = await post('/api/auth/register', {
        name,
        email,
        password
      });
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        setUser(response.user);
      }
      
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to register');
    }
  };

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    isAuthenticated: !!user
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 